import { normalizeWidgetTheme, APIURL, cancelSelectEvent, changeSymbolForWidget, getCSSStyles, getSymbolDropdown, getTraderSMCardHTML, getWidgetDataURI, apiGet, toggleWidgetTheme, updateSelectedSymbolInDropdown, isHeaderPinned, getHeaderPinStateClass, getHeaderPinButtonHTML, setupHeaderPin, getNoDataHTML, isEmptyWidgetData } from './../../helpers';
import { WidgetConfig, WidgetEvent, WidgetEventHandler, WidgetEventType } from './../../types';

export class TraderverseAIAnalysisSentimentChart {
  private container: HTMLElement;
  private config: WidgetConfig;
  private selectedTab: string = '1y';
  private eventHandlers: Map<WidgetEventType, WidgetEventHandler[]> = new Map();
  private updateInterval: number | null = null;
  private shadowRoot: ShadowRoot;
  private currentData: any = null;
  private currentChart: any;
  private widgetResizeObserver: ResizeObserver | null = null;

  constructor(config: WidgetConfig) {
    this.config = { ...this.getDefaultConfig(), ...config };
    this.config.theme = normalizeWidgetTheme(this.config.theme);
    this.container = this.resolveContainer(config.container);

    if ((this.container as any).shadowRoot) {
      this.shadowRoot = (this.container as any).shadowRoot;
    } else {
      this.shadowRoot = this.container.attachShadow({ mode: 'closed' });
    }
    this.init();

    this.widgetResizeObserver = new ResizeObserver(() => {
      this.updateDisplay();
    });
    setTimeout(() => {
      const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
      if (widget) this.widgetResizeObserver?.observe(widget);
    }, 0);
  }

  private fetchSymbols(): Array<{ value: string; label: string }> {
    return [
      { label: 'AAPL', value: 'AAPL' }, { label: 'MSFT', value: 'MSFT' },
      { label: 'GOOGL', value: 'GOOGL' }, { label: 'AMZN', value: 'AMZN' },
      { label: 'NVDA', value: 'NVDA' }, { label: 'META', value: 'META' },
      { label: 'TSLA', value: 'TSLA' }, { label: 'AVGO', value: 'AVGO' },
      { label: 'PEP', value: 'PEP' }, { label: 'COST', value: 'COST' }
    ];
  }

  private getDefaultConfig(): Partial<WidgetConfig> {
    const symbols = this.fetchSymbols();
    return {
      headerPinned: true,
      symbol: symbols[0].value,
      theme: 'default-dark',
      width: '100%',
      height: '400px',
      autosize: true
    };
  }

  private resolveContainer(container: string | HTMLElement): HTMLElement {
    if (typeof container === 'string') {
      const el = document.getElementById(container) || document.querySelector(container);
      if (!el) throw new Error(`Container not found: ${container}`);
      return el as HTMLElement;
    }
    return container;
  }

  private async init(): Promise<void> {
    if (!this.config.symbol) this.config.symbol = this.fetchSymbols()[0].value;
    this.attachStyles();
    this.createWidget();
    await this.injectChartLibrary();
    await this.setupChart(this.config.symbol!);
    this.emit('ready', {});
    cancelSelectEvent(this.shadowRoot);
    this.config.onReady?.();
  }

  private createWidget(): void {
    let themeClass = 'defaultDarkMode';
  let dataTheme = 'default-dark';
  
  if (this.config.theme === 'white') {
    themeClass = 'whiteMode';
    dataTheme = 'white';
  } else if (this.config.theme === 'black') {
    themeClass = 'blackMode';
    dataTheme = 'black';
  } else if (this.config.theme === 'default-lite') {
  themeClass = 'defaultLightMode';
  dataTheme = 'default-lite';
}
    const widgetHtml = `
      <div style="width: ${typeof this.config.width == "number" ? this.config.width+ 'px' : this.config.width  };height: ${typeof this.config.height == "number" ? this.config.height+ 'px' : this.config.height};" class="widgetBox ${themeClass} loading ${getHeaderPinStateClass(this.config)}" data-theme="${dataTheme}" data-theme="${this.config.theme}" data-selected-tab="${this.selectedTab}">
          <div class="headTop">
            <h4 class="title crypto">${this.getTitleIconSvg()} S&P 500 Price and Average Daily Sentiment</h4>
            <div class="actionCol">${getHeaderPinButtonHTML(this.config)}
              <i class="pi pi-ellipsis-v"></i>
            </div>
          </div>
          <div class="inBody">

            <div class="optionsRow">
                <div class="item tabsDv padHt" id="time-tabs">
                </div>  
                <span></span>
                <div class="item">
                </div>
            </div>

            <div class="chartDv" id="PriceCamprisonContainer" style="height:400px">
            </div>

          </div>
        </div>
    `;
    this.shadowRoot.innerHTML = widgetHtml;
    setupHeaderPin(this.shadowRoot, this.config);
  }

  private async attachStyles(): Promise<void> {
    try {
      const styleEl = await getCSSStyles();
      this.shadowRoot.appendChild(styleEl);
    } catch (err) {
      console.error('Failed to load CSS file:', err);
    }
  }

  private async injectChartLibrary(): Promise<boolean> {
    return new Promise<boolean>((resolve) => {
      if ((window as any).echarts) return resolve(true);
      if (this.shadowRoot.querySelector('script[data-chart-lib]')) {
        const script = this.shadowRoot.querySelector('script[data-chart-lib]') as HTMLScriptElement;
        script.onload = () => resolve(true);
      } else {
        const script = document.createElement('script');
        script.src = 'https://cdn.jsdelivr.net/npm/echarts@5/dist/echarts.min.js';
        script.async = true;
        script.setAttribute('data-chart-lib', 'true');
        this.shadowRoot.appendChild(script);
        script.onload = () => resolve(true);
      }
    });
  }

  private async setupChart(symbol?: string, stockInfo?: any): Promise<void> {
    const widgetBox = this.shadowRoot.querySelector('.widgetBox');
  
    try {
  
      const response = await apiGet({
        method: 'GET',
        url: `https://facts.traderverse.io/news/spy_price_market_sentiment?time=today&symbol=SPY`,
        body: { time: 'today', symbol: 'SPY' },
        headers: {
          'Content-Type': 'application/json'
        }
      });
  
      if (!response.ok) throw new Error("Failed to fetch data");
  
      const json = await response.json();
  
      const output: any[] = Array.isArray(json?.data?.output)
        ? json.data.output
        : Array.isArray(json?.output)
          ? json.output
          : [];
  
      this.currentData = {
        ...json,
        result: output.map((item: any) => ({
          publish_date: item.date,
          stock_price: Number(item.spy_price),
          avg_sentiment: Number(item.avg_sentiment),
        })),
      };
  
      this.updateDisplay();
      this.emit('dataUpdate', this.currentData);
  
    } catch (err) {
      console.error("Error fetching chart data:", err);
    } finally {
      widgetBox?.classList.remove('loading');
    }
  }

  private updateDisplay(): void {
    const container = this.shadowRoot.querySelector('#PriceCamprisonContainer') as HTMLElement;
    if (!(window as any).echarts) {
      setTimeout(() => this.updateDisplay(), 100);
      return;
    }
    if (!this.currentData || isEmptyWidgetData(this.currentData.result)) {
      if (container) container.innerHTML = getNoDataHTML();
      return;
    }
    this.renderAnalystRating();
  }

  private renderAnalystRating(): void {
    const container = this.shadowRoot.querySelector('#PriceCamprisonContainer');
    if (!container) return;

    const hasData =
        this.currentData?.result &&
        Array.isArray(this.currentData.result) &&
        this.currentData.result.length > 0;

    const html = `
        <div class="marketRealtimeMain">
            <div class="chartHeader">
                <div class="marketCapRow">
                    <div class="item"></div>
                    <div class="item"></div>
                </div>
            </div>
            <div
                class="chart"
                id="marketRealtimeChart"
                ${hasData ? 'data-testing-id="market-realtime-chart"' : ''}
            ></div>
        </div>
    `;

    container.innerHTML = html;
    this.drawChart();
}

private drawChart(): void {
  const chartContainer = this.shadowRoot.querySelector('#marketRealtimeChart') as HTMLElement;

if (!this.currentData?.result?.length) {
    chartContainer?.removeAttribute('data-testing-id');
    return;
}
  const echarts = (window as any).echarts;
  const container = this.shadowRoot.querySelector('#marketRealtimeChart') as HTMLElement;
  if (!container || !this.currentData?.result?.length) return;

  const data = this.currentData.result;

  // Extract and prepare data (like Vue code)
  const dates = data.map((item: any) => item.publish_date);
  const stockPrices = data.map((item: any) => parseFloat(item.stock_price.toFixed(2)));
  const avgSentiments = data.map((item: any) => parseFloat(item.avg_sentiment.toFixed(2)));

  // Get dynamic Y-min values (same as Vue)
  const stockPriceMin = Math.min(...stockPrices);
  const sentimentMin = Math.min(...avgSentiments);

  const chart = echarts.init(container);

  const option = {
    backgroundColor: 'transparent',
    textStyle: {
      fontFamily: 'Montserrat, sans-serif',
      color: '#7C7C7C',
    },
    grid: {
      left: 60,
      right: 60,
      top: 40,
      bottom: 40,
    },
    tooltip: {
      trigger: 'axis',
      backgroundColor: 'rgba(30, 30, 30, 0.9)',
      borderWidth: 0,
      textStyle: {
        color: '#fff',
        fontSize: 14,
      },
      formatter: (params: any) => {
        const time = params[0].axisValue;
        const lines = params
          .map(
            (p: any) =>
              `<span style="color:${p.color}">●</span> ${p.seriesName}: <b>${p.value}</b>`
          )
          .join('<br/>');
        return `<b>${time}</b><br/>${lines}`;
      },
    },
    legend: {
      show: true,
      bottom: 0,
      textStyle: {
        color: '#7C7C7C',
      },
      data: ['S&P 500 Close Price', 'Average Daily Sentiment Score'],
    },
    xAxis: {
      type: 'category',
      data: dates,
      axisLabel: {
        show: false, // same as Vue: no x-labels
      },
      axisLine: {
        lineStyle: { color: '#444444' },
      },
      axisTick: {
        show: false,
      },
    },
    yAxis: [
      {
        type: 'value',
        name: 'S&P 500 Close Price',
        min: stockPriceMin,
        position: 'left',
        axisLabel: {
          color: '#CDCCCF',
          fontSize: 11,
          fontFamily: 'Poppins',
        },
        nameTextStyle: {
          color: '#CDCCCF',
          fontSize: 12,
        },
        splitLine: {
          show: true,
          lineStyle: { color: 'rgba(255,255,255,0.051)' },
        },
      },
      {
        type: 'value',
        name: 'Average Daily Sentiment Score',
        min: sentimentMin,
        position: 'right',
        axisLabel: {
          color: '#CDCCCF',
          fontSize: 11,
          fontFamily: 'Poppins',
        },
        nameTextStyle: {
          color: '#CDCCCF',
          fontSize: 12,
        },
        splitLine: { show: false },
      },
    ],
    series: [
      {
        name: 'S&P 500 Close Price',
        type: 'line',
        smooth: false,
        yAxisIndex: 0,
        data: stockPrices,
        symbol: 'circle',
        symbolSize: 5,
        lineStyle: {
          width: 2,
          color: '#434099',
        },
        areaStyle: {
          color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
            { offset: 0, color: 'rgba(67, 64, 153, 0.6)' },
            { offset: 1, color: 'rgba(67, 64, 153, 0.2)' },
          ]),
        },
      },
      {
        name: 'Average Daily Sentiment Score',
        type: 'line',
        smooth: false,
        yAxisIndex: 1,
        data: avgSentiments,
        symbol: 'circle',
        symbolSize: 5,
        lineStyle: {
          width: 2,
          color: '#CDCCCF',
        },
        areaStyle: {
          color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
            { offset: 0, color: 'rgba(205, 204, 207, 0.6)' },
            { offset: 1, color: 'rgba(205, 204, 207, 0.2)' },
          ]),
        },
      },
    ],
  };

  chart.setOption(option);
  this.currentChart = chart;
}




  private changeSymbol(stockId: string, stock: any = null): void {
    changeSymbolForWidget(this.shadowRoot, this.config, stock, stockId, (stockId: any, stockInfo: any) => this.setupChart(stockId, stockInfo), (type: any, data: any) => this.emit(type, data));
  }

  public on(type: WidgetEventType, handler: WidgetEventHandler): void {
    if (!this.eventHandlers.has(type)) this.eventHandlers.set(type, []);
    this.eventHandlers.get(type)!.push(handler);
  }
  public off(type: WidgetEventType, handler: WidgetEventHandler): void {
    const list = this.eventHandlers.get(type);
    if (list) this.eventHandlers.set(type, list.filter(h => h !== handler));
  }
  public setSymbol(symbol: string): void {
    this.changeSymbol(symbol);
    const dropdown = this.shadowRoot.querySelector('.symbol-dropdown') as HTMLSelectElement;
    if (dropdown) dropdown.value = symbol;
  }
  public async getWidgetImage(): Promise<string> {
    const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
    return await getWidgetDataURI(widget);
  }
  
  public setTheme(theme: 'white' | 'black' | 'default-dark' | 'default-lite' | 'default-lite'): void {
    if (!this.shadowRoot) return;
  
    this.config.theme = toggleWidgetTheme(
      this.shadowRoot,
      theme
    ) as 'white' | 'black' | 'default-dark' | 'default-lite' | 'default-lite';
  
    this.updateDisplay();
  }
  public destroy(): void {
    if (this.updateInterval) { clearInterval(this.updateInterval); this.updateInterval = null; }
    if (this.widgetResizeObserver) { this.widgetResizeObserver.disconnect(); this.widgetResizeObserver = null; }
    this.eventHandlers.clear(); this.shadowRoot.innerHTML = '';
  }
  private emit(type: WidgetEventType, data: any): void {
    const handlers = this.eventHandlers.get(type);
    if (handlers) { const evt: WidgetEvent = { type, data }; handlers.forEach(h => h(evt)); }
  }
  private getTitleIconSvg(): string {
    return `<svg width="30" height="30" viewBox="0 0 30 30" fill="none" xmlns="http://www.w3.org/2000/svg"> <path d="M15.15 20.3C12.3125 20.3 10 17.9875 10 15.15C10 12.3125 12.3125 10 15.15 10C17.9875 10 20.3 12.3125 20.3 15.15C20.3 17.9875 18 20.3 15.15 20.3ZM15.15 11.8625C13.3375 11.8625 11.875 13.3375 11.875 15.1375C11.875 16.9375 13.35 18.4125 15.15 18.4125C16.95 18.4125 18.425 16.9375 18.425 15.1375C18.425 13.3375 16.9625 11.8625 15.15 11.8625Z" fill="currentColor"/> <path d="M15 28.4375C7.5875 28.4375 1.5625 22.4125 1.5625 15C1.5625 7.5875 7.5875 1.5625 15 1.5625C22.4125 1.5625 28.4375 7.5875 28.4375 15C28.4375 22.4125 22.4125 28.4375 15 28.4375ZM15 3.4375C8.625 3.4375 3.4375 8.625 3.4375 15C3.4375 21.375 8.625 26.5625 15 26.5625C21.375 26.5625 26.5625 21.375 26.5625 15C26.5625 8.625 21.375 3.4375 15 3.4375Z" fill="currentColor"/> <path d="M15.0625 22C14.5158 22 14.0625 21.5192 14.0625 20.9394V9.06061C14.0625 8.48081 14.5158 8 15.0625 8C15.6092 8 16.0625 8.48081 16.0625 9.06061V20.9394C16.0625 21.5192 15.6092 22 15.0625 22Z" fill="currentColor"/> </svg>`;
  }

}

declare global { interface Window { TraderverseAIAnalysisSentimentChart: typeof TraderverseAIAnalysisSentimentChart; } }
if (typeof window !== 'undefined') { window.TraderverseAIAnalysisSentimentChart = TraderverseAIAnalysisSentimentChart; }
